import React from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, setLanguage, languageLocked } from '../i18n';

/**
 * Interface language picker. Writing the choice through setLanguage() makes it
 * the visitor's own — from then on the account's office language no longer
 * overrides it, on this browser.
 *
 * Hidden where it would be a lie: on an edition that serves a single market the
 * language is fixed, and with only one language built in there is nothing to
 * choose. Either way the control does not render at all.
 */

/** Past this many languages a segmented control stops fitting on one line. */
const SEGMENTED_MAX = 5;

const LanguageSwitcher: React.FC<{ className?: string }> = ({ className }) => {
  const { i18n, t } = useTranslation();
  if (languageLocked() || LANGUAGES.length < 2) return null;

  // i18n.language is a bare code here, but a detected 'uk-UA' would not match.
  const current = (i18n.language || '').split('-')[0];

  if (LANGUAGES.length > SEGMENTED_MAX) {
    return (
      <select
        className={className ?? 'field-select lang-select'}
        value={current}
        onChange={e => setLanguage(e.target.value)}
        aria-label={t('common.language')}
      >
        {LANGUAGES.map(l => (
          <option key={l.code} value={l.code}>{l.native}</option>
        ))}
      </select>
    );
  }

  return (
    <div className={className ?? 'lang-seg'} role="radiogroup" aria-label={t('common.language')}>
      {LANGUAGES.map(l => {
        const active = l.code === current;
        return (
          <button
            key={l.code}
            type="button"
            role="radio"
            aria-checked={active}
            className={`lang-seg-item${active ? ' active' : ''}`}
            onClick={() => setLanguage(l.code)}
          >
            <span className="lang-seg-code">{l.code.toUpperCase()}</span>
            {l.native}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
